/**
 * THE WORKSPACE'S ONLY DOOR TO SCOUT — Scout's own `/api/scout/gateway`
 * surface, read exactly as it answers.
 *
 * THIS FILE HOLDS NO CREDENTIAL AND CANNOT BE GIVEN ONE. There is no token
 * parameter, no authorization header, no storage read and no build-time
 * variable: the browser authenticates by Cloudflare Access, which puts a signed
 * assertion on the request at the edge, and a local development server attaches
 * the owner token on the other side of the wire (see `vite.config.ts`). A
 * client that could carry a bearer token is a client that could store one, and
 * this one must not be able to.
 *
 * THE PRODUCT'S OWN ERROR NAMES ARE PRESERVED RATHER THAN FLATTENED. Scout's
 * HTTP surface answers a closed deployment with `ScoutOwnerAuthUnavailable`, a
 * wrong credential with `Unauthorized`, an agent credential on an owner route
 * with `ScoutAgentNotOwner`, and an expired Access session with a
 * `ScoutAccess*` name. Those are DIFFERENT PROBLEMS WITH DIFFERENT FIXES, and a
 * client that rendered them all as "something went wrong" would be throwing
 * away the most useful thing the backend said. So the name and message travel
 * through untouched, and the views decide how to phrase them.
 */

/** The uniform envelope every `/api/scout` route answers with. */
interface Envelope<T> {
	success: boolean;
	data?: T;
	message?: string;
	error?: { message?: string; name?: string };
}

export interface GatewayError {
	/** The backend's own error name where it gave one, or a name this client
	 *  gives a failure that never reached the backend. */
	name: string;
	message: string;
	/** The HTTP status, or 0 when no response arrived at all. */
	status: number;
}

export type GatewayResult<T> =
	| { ok: true; data: T }
	| { ok: false; error: GatewayError };

/** The envelope's own `data`, for a route whose payload this file does not yet
 *  model. Views that read a payload properly declare it; nothing here guesses
 *  at a shape it has not seen. */
export type UnknownPayload = unknown;

export interface RequestOptions {
	/** How long to wait before giving up. Scout's long operations block by
	 *  design (`/gateway/ask` can run a model and a research segment in one
	 *  turn), so a caller that expects a slow answer says so. */
	timeoutMs?: number;
	signal?: AbortSignal;
	method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
	body?: unknown;
}

/**
 * A READ THAT WAS REFUSED FOR WANT OF A SESSION, ANNOUNCED TO WHOEVER IS
 * LISTENING.
 *
 * WHY THE TRANSPORT IS WHERE THIS LIVES. The shell has to learn that a session
 * ended, and the only component that sees EVERY read is this one — a view-level
 * answer would need thirteen of them and would still miss the ones inside
 * `Promise.all`. The registry is here rather than in `session.ts` because the
 * dependency has to point one way: `session.ts` already imports this file, and
 * a module that both imports and is imported by its own dependency is a cycle
 * that works until the day the bundler reorders it.
 *
 * IT IS INERT UNLESS SOMEBODY REGISTERS. Nothing in this file reads the
 * observers' state, none of them can change what a read returns, and a caller
 * that never registers one gets exactly the client that existed before this
 * function did. That is what keeps it an announcement rather than a coupling.
 */
type AuthenticationObserver = (error: GatewayError) => void;

const authenticationObservers = new Set<AuthenticationObserver>();

export function onAuthenticationFailure(observer: AuthenticationObserver): () => void {
	authenticationObservers.add(observer);
	return () => {
		authenticationObservers.delete(observer);
	};
}

function announceAuthenticationFailure(error: GatewayError): void {
	for (const observer of authenticationObservers) {
		try {
			observer(error);
		} catch {
			// AN OBSERVER THAT THROWS IS A BUG IN THE OBSERVER, NOT IN THE READ.
			// Swallowing it here keeps one broken listener from turning every
			// 401 in the workspace into an unhandled rejection.
		}
	}
}

const DEFAULT_TIMEOUT_MS = 30_000;

function failure(name: string, message: string, status: number): GatewayResult<never> {
	return { ok: false, error: { name, message, status } };
}

/** The same refusal, built as a value first — for the two places that have to
 *  announce it to the shell as well as return it. */
function refusalError(name: string, message: string, status: number): GatewayError {
	return { name, message, status };
}

/**
 * Read one gateway route.
 *
 * A 200 THAT DOES NOT CARRY `success: true` IS A FAILURE, not a payload. The
 * envelope is the product's contract; a client that read `data` off a body
 * whose `success` was false would render an error object as if it were a
 * research record.
 */
export async function readGateway<T = UnknownPayload>(
	path: string,
	options: RequestOptions = {},
): Promise<GatewayResult<T>> {
	const method = options.method ?? 'GET';
	const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
	const timeout = AbortSignal.timeout(timeoutMs);
	// THE CALLER'S SIGNAL AND THE TIMEOUT BOTH APPLY. `AbortSignal.any` keeps
	// them independent, so a view that cancels its own request does not have to
	// know the timeout exists and vice versa.
	const signal = options.signal === undefined ? timeout : AbortSignal.any([options.signal, timeout]);

	let response: Response;
	try {
		response = await fetch(path, {
			method,
			signal,
			// The Access session is a cookie the browser holds for this origin;
			// it is the edge's credential, not this client's, and this client
			// never reads it.
			credentials: 'same-origin',
			// THE REDIRECT IS NOT FOLLOWED, BECAUSE FOLLOWING IT IS WHAT MADE THIS
			// CLIENT LIE. A signed-out browser asking a protected path is answered
			// by the identity provider with a redirect to its login page. Following
			// it blindly, as the default does, ends in either an HTML page parsed as
			// JSON — "Scout's answer was not the envelope its API promises" — or a
			// cross-origin fetch that CORS refuses, which is indistinguishable from
			// the network being down. Phase 6A measured both outcomes and they are
			// the same wrong answer: the Owner is told Scout could not be reached,
			// and the sign-in control goes inert.
			//
			// In `manual` mode the browser hands back an opaque-redirect response
			// instead: no headers, no body, `status: 0`, `type: 'opaqueredirect'`.
			// The one thing it does say is the thing this client needs — SOMETHING
			// ANSWERED, AND IT ASKED FOR A SESSION RATHER THAN REFUSING. That is a
			// fact about the edge, and it is the only fact available.
			redirect: 'manual',
			headers:
				options.body === undefined ? {} : { 'content-type': 'application/json' },
			...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
		});
	} catch (error) {
		// NO RESPONSE AT ALL IS ITS OWN ANSWER, and it is not the same as a
		// refusal. Offline, a dropped connection and a DNS failure all land
		// here and all mean "Scout was not reached", which is a different thing
		// to tell the Owner than "Scout refused you".
		if (error instanceof DOMException && error.name === 'TimeoutError') {
			return failure(
				'ScoutRequestTimedOut',
				`Scout did not answer within ${Math.round(timeoutMs / 1000)} seconds.`,
				0,
			);
		}
		if (error instanceof DOMException && error.name === 'AbortError') {
			return failure('ScoutRequestAbandoned', 'The request was abandoned before Scout answered.', 0);
		}
		return failure(
			'ScoutUnreachable',
			'Scout could not be reached at all — the request never arrived.',
			0,
		);
	}

	// THE EDGE ASKED FOR A SESSION, AND THE ASK IS THE ANSWER. Checked before the
	// body is read, because in `manual` mode there is no body to read — and
	// checked here rather than in the caller because this is the one place that
	// sees every read.
	//
	// TWO TESTS FOR ONE FACT, AND THE SECOND IS NOT REDUNDANT. `opaqueredirect` is
	// what the Fetch standard specifies for a manual redirect in every current
	// browser. The status test catches an engine that hands back the real response
	// instead (which some did historically for same-origin redirects), so the
	// classification does not depend on which one is running. A 3xx is never a
	// Scout answer either way: no route in this product redirects.
	if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
		const edgeFailure = refusalError(
			'ScoutEdgeRedirected',
			`Scout's API answered with a redirect (HTTP ${response.status >= 300 && response.status < 400 ? response.status : 302}). That is not Scout refusing and it is not the network: it is the deployment's edge asking this browser for a session, and the request never reached Scout.`,
			// 302 IS THE REPRESENTATIVE CODE AND THE NAME IS THE FACT. `manual` mode
			// deliberately hides the real status, so reporting the exact one would be
			// inventing it; the only redirect this deployment's edge issues on this
			// path is Access's own, which is a 302.
			response.status >= 300 && response.status < 400 ? response.status : 302,
		);
		announceAuthenticationFailure(edgeFailure);
		return { ok: false, error: edgeFailure };
	}

	let envelope: Envelope<T> | null = null;
	try {
		envelope = (await response.json()) as Envelope<T>;
	} catch {
		envelope = null;
	}

	if (envelope === null || typeof envelope !== 'object') {
		// A NON-JSON ANSWER. Every Scout route answers the envelope, so this
		// means the request did not reach Scout at all — an edge page, a proxy,
		// a captive portal — and saying so is more useful than "invalid JSON".
		//
		// AN EDGE THAT ANSWERS 401 IS AN EDGE THAT WANTS A SESSION. Cloudflare
		// Access refuses an API request with a 401 and a JSON body of its own,
		// and one that is configured to redirect answers HTML at a status this
		// branch may still land on. Either way the shell needs to hear it, so
		// the announcement is made on the STATUS before the shape is judged.
		const edgeFailure = refusalError(
			'ScoutAnswerNotUnderstood',
			`Scout's answer was not the envelope its API promises (HTTP ${response.status}). The request may not have reached Scout.`,
			response.status,
		);
		if (response.status === 401) announceAuthenticationFailure(edgeFailure);
		return { ok: false, error: edgeFailure };
	}

	if (envelope.success !== true) {
		const refusal = refusalError(
			typeof envelope.error?.name === 'string' && envelope.error.name.length > 0
				? envelope.error.name
				: 'ScoutRequestRefused',
			typeof envelope.error?.message === 'string' && envelope.error.message.length > 0
				? envelope.error.message
				: `Scout refused the request (HTTP ${response.status}) without giving a reason.`,
			response.status,
		);
		if (response.status === 401) announceAuthenticationFailure(refusal);
		return { ok: false, error: refusal };
	}

	return { ok: true, data: envelope.data as T };
}

/**
 * WHETHER A FAILURE MEANS "YOU ARE NOT SIGNED IN", which the shell answers with
 * the sign-in state rather than an error panel.
 *
 * THE STATUS IS THE SIGNAL FOR EVERY REFUSAL SCOUT ITSELF ISSUES, AND THE GATE
 * IS BUILT SO THAT IT IS. Every way an Access assertion can fail — expired,
 * signed by nobody, issued for another application, naming an identity that is
 * not the Owner's — is answered 401, while 403 means "authenticated, and not
 * permitted". That line is the HTTP one, and `owner-access.runtime.test.ts`
 * drives every refusal through the real app to hold it.
 *
 * A NAME CHECK WOULD BE THE WRONG SHAPE FOR THOSE. Grouping the `ScoutAccess*`
 * refusals by prefix would sweep in `ScoutAccessServiceNotOwner` — a valid
 * session belonging to a machine — and tell an Owner to sign in again when the
 * answer is that a service token is not a person.
 *
 * AND THERE IS A SECOND WAY TO HAVE NO SESSION, WHICH NEVER REACHES SCOUT AT
 * ALL: the edge answers the request itself, with a redirect to the identity
 * provider's login. No status, no envelope and no Scout error name exists in
 * that case — the request was stopped one layer out — so it is named here, by
 * the only party that can see it. Both are the same fact about this browser:
 * there is no session, and there is a door.
 */
export function isAuthenticationFailure(error: GatewayError): boolean {
	return error.status === 401 || error.name === 'ScoutEdgeRedirected';
}

/**
 * WHETHER A FAILURE MEANS "SIGNED IN, BUT THIS IS NOT THE OWNER'S ACCOUNT".
 *
 * A DIFFERENT ANSWER, DELIBERATELY. This is not a session that can be renewed
 * by signing in again — it is the wrong person, and telling them to retry would
 * be an invitation to keep trying.
 */
export function isNotTheOwner(error: GatewayError): boolean {
	return (
		error.name === 'ScoutAccessNotOwner' ||
		error.name === 'ScoutAccessServiceNotOwner' ||
		error.name === 'ScoutAgentNotOwner'
	);
}

/**
 * IS ANYTHING ANSWERING AT ALL — ASKED WITHOUT A SESSION, ON PURPOSE.
 *
 * `GET /api/health` is the product's only unauthenticated route and the only
 * one that does not carry the envelope: it answers `{status, product}` and
 * nothing else. That shape is what makes it useful HERE rather than a nuisance.
 *
 * WHY IT IS NOT `readGateway`. The question this answers is "did the request
 * reach a running Scout", and the interesting case is the one where the
 * authenticated reads all failed. If this went through the session, a signed-out
 * Owner would see one undifferentiated failure and could not tell a deployment
 * that is down from a session that expired — which are fixed by different
 * people. It is asked PLAINLY and its answer is reported as what it is:
 * LIVENESS. A 200 here proves a Worker answered. It does not prove Scout can
 * research anything, and nothing on the screen that shows this is allowed to
 * imply otherwise.
 */
export type LivenessResult =
	| { state: 'answering'; product: string | null; at: string }
	| { state: 'unreachable'; detail: string }
	| { state: 'unexpected'; detail: string };

export async function probeLiveness(): Promise<LivenessResult> {
	const at = new Date().toISOString();
	let response: Response;
	try {
		response = await fetch('/api/health', {
			method: 'GET',
			signal: AbortSignal.timeout(10_000),
			credentials: 'same-origin',
			// NOT FOLLOWED, FOR THE REASON `readGateway` GIVES — and reported as
			// its own answer below rather than as a bare odd status. This route is
			// deliberately ungated, so a redirect here means the edge has been
			// configured to cover the whole hostname; that is a deployment fact the
			// reader should be told plainly, not a mystery code.
			redirect: 'manual',
		});
	} catch (error) {
		return {
			state: 'unreachable',
			detail:
				error instanceof DOMException && error.name === 'TimeoutError'
					? 'the request did not receive an answer within 10 seconds'
					: 'the request never arrived',
		};
	}
	if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
		return {
			state: 'unexpected',
			detail: 'the edge answered with a redirect instead of letting the request reach Scout',
		};
	}
	if (!response.ok) {
		return { state: 'unexpected', detail: `it answered HTTP ${response.status}` };
	}
	try {
		const body = (await response.json()) as { status?: unknown; product?: unknown };
		// A 200 THAT IS NOT THIS ROUTE'S SHAPE IS NOT A HEALTHY SCOUT. An edge
		// page, a proxy or a captive portal answers 200 with HTML, and reading
		// that as "Scout is up" would be the most confident possible wrong answer.
		if (body.status !== 'ok') {
			return { state: 'unexpected', detail: 'it answered 200 with a body that is not this route’s' };
		}
		return {
			state: 'answering',
			product: typeof body.product === 'string' ? body.product : null,
			at,
		};
	} catch {
		return { state: 'unexpected', detail: 'it answered 200 with a body that is not JSON' };
	}
}
